import { useState, useCallback, useEffect, type FC } from 'react';
import { useCustom, useCustomMutation } from '@refinedev/core';
import { Button, Spin, Modal, Input, DatePicker, message } from 'antd';
import { Map, Plus } from 'lucide-react';
import { BilingualLabel } from '@/components/common/BilingualLabel';
import { GuidedTour } from '@/components/onboarding/GuidedTour';
import { usePageTour } from '@/hooks/usePageTour';
import { roadmapTourSteps } from '@/lib/tour-data';
import RoadmapProgressBar from './components/RoadmapProgressBar';
import RoadmapDetailView from './components/RoadmapDetailView';
import type { Dayjs } from 'dayjs';

interface RoadmapSummary {
  id: string;
  title: string;
  description?: string | null;
  status: string;
  startDate: string;
  endDate?: string | null;
  milestonesTotal: number;
  milestonesCompleted: number;
  createdAt: string;
}

const STATUS_LABEL: Record<string, string> = {
  draft: 'Brouillon',
  active: 'Active',
  completed: 'Terminee',
  archived: 'Archivee',
};

const STATUS_COLOR: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  active: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  archived: 'bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
};

const TOUR_ID = 'admin-growth-roadmap';

const RoadmapPage: FC = () => {
  const tour = usePageTour(TOUR_ID);
  useEffect(() => {
    const handler = () => tour.startTour();
    window.addEventListener('restart-page-tour', handler);
    return () => window.removeEventListener('restart-page-tour', handler);
  }, [tour]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', startDate: '', endDate: '' });
  const { mutate: createRoadmap, isLoading: isCreating } = useCustomMutation();

  const { data, isLoading, refetch } = useCustom<{ data: RoadmapSummary[] }>({
    url: '/api/v1/admin/growth/roadmaps',
    method: 'get',
    queryOptions: { queryKey: ['roadmaps'] },
  });

  const roadmaps: RoadmapSummary[] = (data?.data as unknown as { data: RoadmapSummary[] })?.data ?? [];

  const handleCreate = useCallback(() => {
    if (!form.title.trim() || !form.startDate) return;
    createRoadmap(
      { url: '/api/v1/admin/growth/roadmaps', method: 'post', values: { title: form.title, description: form.description || null, startDate: form.startDate, endDate: form.endDate || null } },
      {
        onSuccess: () => { message.success('Feuille de route creee'); setCreateOpen(false); setForm({ title: '', description: '', startDate: '', endDate: '' }); refetch(); },
        onError: () => message.error('Erreur lors de la creation'),
      },
    );
  }, [form, createRoadmap, refetch]);

  // Detail view
  if (selectedId) {
    return <RoadmapDetailView roadmapId={selectedId} onBack={() => setSelectedId(null)} />;
  }

  return (
    <div className="space-y-6">
      {/* Header gradient */}
      <div className="fc-gradient-header rounded-xl p-6 flex items-center justify-between flex-wrap gap-4" data-tour="roadmap-header">
        <div className="flex items-center gap-3">
          <Map className="w-6 h-6 text-white/80" />
          <div>
            <BilingualLabel glossaryKey="roadmap" size="lg" />
          </div>
        </div>
        <Button type="primary" icon={<Plus className="w-4 h-4" />} onClick={() => setCreateOpen(true)}>
          Nouvelle feuille de route
        </Button>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex h-40 items-center justify-center"><Spin size="large" /></div>
      ) : roadmaps.length === 0 ? (
        <div className="fc-card p-12 text-center">
          <Map className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-slate-400">Aucune feuille de route. Creez-en une pour commencer.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-tour="roadmap-list">
          {roadmaps.map((r) => (
            <button key={r.id} onClick={() => setSelectedId(r.id)} className="fc-card p-5 text-left space-y-3 hover:ring-2 hover:ring-blue-300 dark:hover:ring-blue-600 transition-all cursor-pointer">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-slate-900 dark:text-white text-sm truncate">{r.title}</h3>
                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${STATUS_COLOR[r.status] ?? STATUS_COLOR.draft}`}>
                  {STATUS_LABEL[r.status] ?? r.status}
                </span>
              </div>
              {r.description && <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2">{r.description}</p>}
              <RoadmapProgressBar completed={r.milestonesCompleted} total={r.milestonesTotal} />
              <div className="flex items-center gap-3 text-[10px] text-slate-400 dark:text-slate-500">
                <span>{r.startDate}{r.endDate ? ` - ${r.endDate}` : ''}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Create modal */}
      <Modal
        title="Nouvelle feuille de route"
        open={createOpen}
        onOk={handleCreate}
        onCancel={() => setCreateOpen(false)}
        confirmLoading={isCreating}
        okText="Creer"
        cancelText="Annuler"
      >
        <div className="space-y-4 pt-2">
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Titre *</label>
            <Input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="Ex: Lancement Beta Q2 2026" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Description</label>
            <Input.TextArea rows={2} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Date de debut *</label>
              <DatePicker className="w-full" format="YYYY-MM-DD" onChange={(_: Dayjs | null, dateStr: string | string[]) => { const v = Array.isArray(dateStr) ? dateStr[0] : dateStr; setForm((p) => ({ ...p, startDate: v })); }} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Date de fin</label>
              <DatePicker className="w-full" format="YYYY-MM-DD" onChange={(_: Dayjs | null, dateStr: string | string[]) => { const v = Array.isArray(dateStr) ? dateStr[0] : dateStr; setForm((p) => ({ ...p, endDate: v })); }} />
            </div>
          </div>
        </div>
      </Modal>
      <GuidedTour tourId={TOUR_ID} steps={roadmapTourSteps} isActive={tour.isActive} onComplete={tour.completeTour} onSkip={tour.skipTour} />
    </div>
  );
};

export default RoadmapPage;
